import React from 'react';
import {
  Box,
  Chip,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  Award,
  Brain,
  CheckCircle2,
  Sparkles,
} from 'lucide-react';

export interface ISkillsTabProps {
  isHighlightHit: (item: string) => boolean;
  skills?: string[];
  matchedSkills?: string[];
  behavioralAttributes?: string[];
  matchedBehavior?: string[];
}

export const SkillsTab = ({
  isHighlightHit,
  skills,
  matchedSkills,
  behavioralAttributes,
  matchedBehavior,
}: ISkillsTabProps) => {
  return (
    <Box sx={{ py: 1.5, display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* 1. All Skills */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: '12px',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <Box
            sx={{
              p: 0.75,
              borderRadius: '8px',
              bgcolor: 'rgba(37, 99, 235, 0.08)',
              color: '#2563eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Sparkles size={16} />
          </Box>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, fontSize: '14px', color: 'text.primary' }}
          >
            All Skills
          </Typography>
          <Chip
            label={skills?.length || 0}
            size="small"
            sx={{ height: '20px', fontSize: '11px', fontWeight: 600, bgcolor: 'grey.100' }}
          />
        </Stack>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            p: 1.5,
            bgcolor: 'grey.50',
            borderRadius: '8px',
            border: '1px solid',
            borderColor: 'grey.200',
            minHeight: '44px',
            alignItems: 'center',
          }}
        >
          {skills && skills.length > 0 ? (
            skills.map((skill, i) => {
              const isHit = isHighlightHit(skill);
              return (
                <Chip
                  key={i}
                  label={skill}
                  size="small"
                  sx={{
                    fontSize: '12px',
                    fontWeight: isHit ? 700 : 500,
                    bgcolor: isHit ? 'primary.main' : 'background.paper',
                    color: isHit ? '#ffffff' : 'text.primary',
                    border: '1px solid',
                    borderColor: isHit ? 'primary.main' : 'grey.300',
                    boxShadow: isHit ? '0 2px 4px rgba(37, 99, 235, 0.25)' : 'none',
                    transition: 'all 0.2s ease',
                  }}
                />
              );
            })
          ) : (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              No skills available
            </Typography>
          )}
        </Box>
      </Paper>

      {/* 2. Matched Skills */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: '12px',
          border: '1px solid',
          borderColor: 'rgba(16, 185, 129, 0.3)',
          bgcolor: 'rgba(16, 185, 129, 0.02)',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <Box
            sx={{
              p: 0.75,
              borderRadius: '8px',
              bgcolor: 'rgba(16, 185, 129, 0.1)',
              color: '#059669',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CheckCircle2 size={16} />
          </Box>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, fontSize: '14px', color: '#065f46' }}
          >
            Matched Skills
          </Typography>
          <Chip
            label={matchedSkills?.length || 0}
            size="small"
            sx={{
              height: '20px',
              fontSize: '11px',
              fontWeight: 600,
              bgcolor: 'rgba(16, 185, 129, 0.12)',
              color: '#047857',
            }}
          />
        </Stack>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            p: 1.5,
            bgcolor: 'background.paper',
            borderRadius: '8px',
            border: '1px solid',
            borderColor: 'rgba(16, 185, 129, 0.2)',
            minHeight: '44px',
            alignItems: 'center',
          }}
        >
          {matchedSkills && matchedSkills.length > 0 ? (
            matchedSkills.map((skill, i) => (
              <Chip
                key={i}
                label={skill}
                size="small"
                icon={<CheckCircle2 size={13} color="#ffffff" />}
                sx={{
                  fontSize: '12px',
                  fontWeight: 600,
                  bgcolor: '#059669',
                  color: '#ffffff',
                  boxShadow: '0 2px 4px rgba(5, 150, 105, 0.2)',
                }}
              />
            ))
          ) : (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              No matched skills for this job opening
            </Typography>
          )}
        </Box>
      </Paper>

      {/* 3. Behavioral Attributes */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: '12px',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <Box
            sx={{
              p: 0.75,
              borderRadius: '8px',
              bgcolor: 'rgba(234, 88, 12, 0.08)',
              color: '#ea580c',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Brain size={16} />
          </Box>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, fontSize: '14px', color: 'text.primary' }}
          >
            Behavioral Attributes
          </Typography>
          <Chip
            label={behavioralAttributes?.length || 0}
            size="small"
            sx={{ height: '20px', fontSize: '11px', fontWeight: 600, bgcolor: 'grey.100' }}
          />
        </Stack>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            p: 1.5,
            bgcolor: 'grey.50',
            borderRadius: '8px',
            border: '1px solid',
            borderColor: 'grey.200',
            minHeight: '44px',
            alignItems: 'center',
          }}
        >
          {behavioralAttributes && behavioralAttributes.length > 0 ? (
            behavioralAttributes.map((attr, i) => {
              const isHit = isHighlightHit(attr);
              return (
                <Chip
                  key={i}
                  label={attr}
                  size="small"
                  sx={{
                    fontSize: '12px',
                    fontWeight: isHit ? 700 : 500,
                    bgcolor: isHit ? '#ea580c' : 'background.paper',
                    color: isHit ? '#ffffff' : 'text.primary',
                    border: '1px solid',
                    borderColor: isHit ? '#ea580c' : 'grey.300',
                    boxShadow: isHit ? '0 2px 4px rgba(234, 88, 12, 0.25)' : 'none',
                    transition: 'all 0.2s ease',
                  }}
                />
              );
            })
          ) : (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              No behavioral attributes recorded
            </Typography>
          )}
        </Box>
      </Paper>

      {/* 4. Matched Behavioral Attributes */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: '12px',
          border: '1px solid',
          borderColor: 'rgba(234, 88, 12, 0.3)',
          bgcolor: 'rgba(234, 88, 12, 0.02)',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <Box
            sx={{
              p: 0.75,
              borderRadius: '8px',
              bgcolor: 'rgba(234, 88, 12, 0.1)',
              color: '#ea580c',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Award size={16} />
          </Box>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, fontSize: '14px', color: '#9a3412' }}
          >
            Matched Behavioral Attributes
          </Typography>
          <Chip
            label={matchedBehavior?.length || 0}
            size="small"
            sx={{
              height: '20px',
              fontSize: '11px',
              fontWeight: 600,
              bgcolor: 'rgba(234, 88, 12, 0.12)',
              color: '#c2410c',
            }}
          />
        </Stack>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            p: 1.5,
            bgcolor: 'background.paper',
            borderRadius: '8px',
            border: '1px solid',
            borderColor: 'rgba(234, 88, 12, 0.2)',
            minHeight: '44px',
            alignItems: 'center',
          }}
        >
          {matchedBehavior && matchedBehavior.length > 0 ? (
            matchedBehavior.map((skill, i) => (
              <Chip
                key={i}
                label={skill}
                size="small"
                icon={<CheckCircle2 size={13} color="#ffffff" />}
                sx={{
                  fontSize: '12px',
                  fontWeight: 600,
                  bgcolor: '#ea580c',
                  color: '#ffffff',
                  boxShadow: '0 2px 4px rgba(234, 88, 12, 0.2)',
                }}
              />
            ))
          ) : (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              No matched behavioral attributes available
            </Typography>
          )}
        </Box>
      </Paper>
    </Box>
  );
};
